import { useQuery, UseQueryResult } from "@tanstack/react-query";
import getConfig from "@/utils/getConfig";

export type TimeFilter = "day" | "week" | "month" | "year" | "all";

interface RedemptionTransactionHistoryItem {
    id: string;
    lootboxName: string;
    prizeName: string;
    prizeValue?: number;
    prizeType: string;
    prizeImage?: string;
    burnedAt: string;
    createdAt: string;
    description: string; // "LootBox Name - Prize Name" format
}

interface RedemptionTransactionHistoryResponse {
    transactions: RedemptionTransactionHistoryItem[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
    timeFilter: string;
    startDate: string;
    endDate: string;
}

interface UseGetTransactionHistoryRedemptionsParams {
    timeFilter?: TimeFilter;
    enabled?: boolean;
}

export const useGetTransactionHistoryRedemptions = (
    fullToken: string | undefined,
    params: UseGetTransactionHistoryRedemptionsParams = {},
): UseQueryResult<RedemptionTransactionHistoryResponse, Error> => {
    const { timeFilter = "all", enabled = true } = params;

    return useQuery({
        queryKey: ["transactionHistoryRedemptions-v1", fullToken, timeFilter],
        queryFn: async (): Promise<RedemptionTransactionHistoryResponse> => {
            if (!fullToken) {
                throw new Error("Full token is required for redemption transaction history");
            }

            const config = getConfig({ token: fullToken });
            const url = new URL("/devourplay/transaction-history-lootbox-redemption", config.basePath);

            // Add time filter to query params
            if (timeFilter !== "all") {
                url.searchParams.append("timeFilter", timeFilter);
            }

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${fullToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                // Special handling for 404 - endpoint not deployed yet
                if (response.status === 404) {
                    console.warn("LootBox redemptions endpoint not deployed yet. Returning empty data.");
                    return {
                        transactions: [],
                        pagination: {
                            total: 0,
                            limit: 50,
                            offset: 0,
                            hasMore: false,
                        },
                        timeFilter: timeFilter,
                        startDate: new Date().toISOString(),
                        endDate: new Date().toISOString(),
                    };
                }

                const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json();
        },
        enabled: enabled && !!fullToken,
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: false,
    });
};